import React, { useMemo, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, Edit3, MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { EMAIL_TEMPLATE_CATEGORIES } from '../../lib/emailTemplateCategories';
import { inScope, sharingText } from '../../lib/adminTemplateScopes';
import { formatDateInZone, parseInstant } from '../../lib/timezone';

export const TEMPLATE_VISIBILITY_BADGE = {
    organization: { label: 'Org-wide', cls: 'bg-violet-100 text-violet-700' },
    shared: { label: 'Shared', cls: 'bg-amber-100 text-amber-700' },
    private: { label: 'Private', cls: 'bg-stone-100 text-stone-600' },
};

export function VisibilityBadge({ visibility }) {
    const b = TEMPLATE_VISIBILITY_BADGE[visibility] || TEMPLATE_VISIBILITY_BADGE.private;
    return <span className={`text-xs px-2 py-0.5 rounded font-medium ${b.cls}`}>{b.label}</span>;
}

const categoryLabel = (value) =>
    (EMAIL_TEMPLATE_CATEGORIES.find((c) => c.value === value) || { label: value || 'General' }).label;

const ownerKey = (t) => (t.is_admin_template ? 'admin' : t.user_id || '');

// The API sends tz-naive UTC timestamps; `parseInstant` reads them as UTC (a bare
// `new Date()` would read them as browser-local and show the wrong day), and the
// date is shown in the viewer's profile zone (`useDisplayZone`, epic #532).
function lastEdit(t, zone) {
    const at = t.updated_at || t.created_at;
    const instant = parseInstant(at);
    return {
        name: t.updated_by_name || t.created_by_name || '—',
        date: formatDateInZone(at, { zone }),
        time: instant ? instant.getTime() : 0,
    };
}

const equalsFilter = (row, columnId, value) => !value || value === 'all' || row.getValue(columnId) === value;

function SortHeader({ column, children }) {
    const sorted = column.getIsSorted();
    return (
        <button
            type="button"
            onClick={column.getToggleSortingHandler()}
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide"
        >
            {children}
            {sorted === 'asc' ? <ArrowUp className="w-3 h-3" />
                : sorted === 'desc' ? <ArrowDown className="w-3 h-3" />
                    : <ArrowUpDown className="w-3 h-3 opacity-40" />}
        </button>
    );
}

/**
 * The org's template library as a TanStack table (#978). Scope is the table's
 * global filter; category, visibility and owner are column filters, so every
 * filter combines and `getFilteredRowModel()` is what "N templates" counts.
 */
export function useAdminTemplatesTable({ data, scope, filters, groupNames, zone, onEdit, onDelete }) {
    const [sorting, setSorting] = useState([]);

    const columns = useMemo(() => [
        {
            id: 'name',
            accessorFn: (t) => t.name || '',
            header: ({ column }) => <SortHeader column={column}>Template</SortHeader>,
            cell: ({ row }) => (
                <div className="min-w-0">
                    <p className="font-medium text-stone-800 truncate">{row.original.name}</p>
                    <p className="text-xs text-stone-500 truncate max-w-sm">{row.original.subject}</p>
                </div>
            ),
        },
        {
            id: 'owner',
            accessorFn: ownerKey,
            filterFn: equalsFilter,
            header: () => <span className="text-xs font-semibold uppercase tracking-wide">Owner</span>,
            cell: ({ row }) => (
                <div className="min-w-0">
                    <p className="text-stone-700 truncate">
                        {row.original.is_admin_template ? 'Admin template' : (row.original.owner_name || '—')}
                    </p>
                    <p className="text-xs text-stone-500 truncate">{sharingText(row.original, groupNames)}</p>
                </div>
            ),
        },
        {
            id: 'category',
            accessorFn: (t) => t.category || 'general',
            filterFn: equalsFilter,
            header: () => <span className="text-xs font-semibold uppercase tracking-wide">Category</span>,
            cell: ({ getValue }) => <span className="text-stone-600">{categoryLabel(getValue())}</span>,
        },
        {
            id: 'visibility',
            accessorFn: (t) => t.visibility || (t.is_admin_template ? 'organization' : 'private'),
            filterFn: equalsFilter,
            header: () => <span className="text-xs font-semibold uppercase tracking-wide">Visibility</span>,
            cell: ({ getValue }) => <VisibilityBadge visibility={getValue()} />,
        },
        {
            id: 'updated',
            accessorFn: (t) => lastEdit(t).time,
            header: ({ column }) => <SortHeader column={column}>Last edited</SortHeader>,
            cell: ({ row }) => {
                const e = lastEdit(row.original, zone);
                return (
                    <div className="whitespace-nowrap" data-testid={`template-last-edited-${row.original.template_id}`}>
                        <p className="text-stone-600">{e.name}</p>
                        <p className="text-xs text-stone-500">{e.date}</p>
                    </div>
                );
            },
        },
        {
            id: 'actions',
            enableSorting: false,
            header: () => <span className="sr-only">Actions</span>,
            // `modal={false}` (as in calendar/EventQuickView.jsx and wiki/WikiActionsMenu.jsx):
            // a modal menu that closes as its item opens the edit Dialog or delete
            // AlertDialog dismisses that dialog straight away.
            cell: ({ row }) => (
                <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-stone-600"
                            aria-label={`Actions for ${row.original.name}`}
                            data-testid={`row-menu-trigger-${row.original.template_id}`}
                        >
                            <MoreHorizontal />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onSelect={() => onEdit(row.original)} data-testid="row-menu-edit">
                            <Edit3 /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onSelect={() => onDelete(row.original)}
                            className="text-red-600 focus:text-red-600 focus:bg-red-50"
                            data-testid="row-menu-delete"
                        >
                            <Trash2 /> Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ], [groupNames, zone, onEdit, onDelete]);

    const columnFilters = useMemo(() => [
        { id: 'category', value: filters.category },
        { id: 'visibility', value: filters.visibility },
        { id: 'owner', value: filters.owner },
    ], [filters.category, filters.visibility, filters.owner]);

    return useReactTable({
        data,
        columns,
        state: { sorting, columnFilters, globalFilter: scope },
        onSortingChange: setSorting,
        globalFilterFn: (row, _columnId, value) => inScope(row.original, value),
        getColumnCanGlobalFilter: () => true,
        getRowId: (t) => t.template_id,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
}

export default function AdminTemplatesTable({ table }) {
    return (
        <div className="border border-stone-200 rounded-xl overflow-hidden">
            <Table className="min-w-[760px]">
                <TableHeader className="bg-stone-50">
                    {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id} className="hover:bg-transparent">
                            {hg.headers.map((h) => (
                                <TableHead key={h.id} className={h.column.id === 'actions' ? 'w-12' : ''}>
                                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id} data-testid={`email-template-${row.original.template_id}`}>
                            {row.getVisibleCells().map((cell) => (
                                <TableCell
                                    key={cell.id}
                                    className={`align-top ${cell.column.id === 'actions' ? 'text-right' : ''}`}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
